import { SIDE_PROFILE_MM, BOTTOM_PROFILE_MM, validateLeafGeometry } from './doorLeafGeometry.js';
import { cassetteProfileMm } from './windowCassetteGeometry.js';
import { defaultWindowLayout, anchoredOpening } from './windowLayout.js';
import { THRESHOLDS, validateFrameDesign } from './doorFrameGeometry.js';
import { DEFAULT_GROOVES } from './leafGrooves.js';
import { DOOR_FAMILIES, doorFamilyDefaults, migrateDoorConstruction, isTimberFamily } from './doorFamily.js';
import { readCottagePattern } from './timberLeafSurface.js';
import { assemblyClearances } from './assemblyClearance.js';
import { readDoorHardware } from './doorManufacturing.js';

export function initialLeafDesign() {
  return { version: 2, name: 'Three windows · 60 mm', widthMm: 842, heightMm: 2057,
    handing: 'left', grooves: { ...DEFAULT_GROOVES }, cottagePattern: 'chevron',
    openings: Array.from({ length: 3 }, () => ({ widthMm: 180, heightMm: 180,
      anchorX: { edge: 'center', offsetMm: 0 }, anchorY: { edge: 'start', offsetMm: 220 } })),
    windowLayout: defaultWindowLayout(), ...doorFamilyDefaults() };
}

// Version 1 coordinates retain their position and bottom/left anchoring.
// Never silently replace a saved custom contour with a radius approximation.
export function migrateLeafDesign(input) {
  if (!input || ![1, 2].includes(input.version)) throw new Error('Configuration versions 1 and 2 are supported.');
  input = { ...migrateDoorConstruction(input), cottagePattern: readCottagePattern(input.cottagePattern) };
  if (input.version === 2) return { ...doorFamilyDefaults(input.family || '60_OUT'), ...input,
    topProfileMm: input.topProfileMm || input.sideProfileMm };
  if (!Array.isArray(input.openings)) throw new Error('Configuration requires an openings array.');
  return { ...doorFamilyDefaults(), ...input, version: 2, openings: input.openings.map(anchoredOpening),
    windowLayout: { ...defaultWindowLayout(), mode: 'individual' },
    sideProfileMm: input.sideProfileMm || SIDE_PROFILE_MM,
    topProfileMm: input.topProfileMm || input.sideProfileMm || SIDE_PROFILE_MM,
    bottomProfileMm: input.bottomProfileMm || BOTTOM_PROFILE_MM };
}

export function validateLeafDesign(design) {
  if (!design || design.version !== 2 || typeof design.name !== 'string' ||
      !Array.isArray(design.openings) || !Array.isArray(design.sideProfileMm) ||
      !Array.isArray(design.bottomProfileMm) || !design.cassette || !design.windowLayout) {
    throw new Error('Configuration requires a name, dimensions, openings, windowLayout, edge profiles and cassette.');
  }
  if (design.widthMm > 4000 || design.heightMm > 4000) throw new Error('Maximum editor dimension: 4000 mm.');
  if (design.family && !DOOR_FAMILIES.some(([id]) => id === design.family)) throw new Error('Unknown door family.');
  if (design.finish !== undefined && !['painted','untreated'].includes(design.finish)) throw new Error('Choose Painted or Untreated finish.');
  if (design.finish === 'untreated' && !isTimberFamily(design.family)) throw new Error('Untreated finish is available for timber door families.');
  if (design.family === 'COTTAGE' && design.finish === 'painted') throw new Error('Cottage uses natural pine panels and trims.');
  readCottagePattern(design.cottagePattern);
  readDoorHardware(design.hardware);
  if (design.threshold && !THRESHOLDS[design.threshold]) throw new Error('Unknown threshold.');
  if (design.handing && !['left', 'right'].includes(design.handing)) throw new Error('Unknown handing.');
  if (design.opening && !['inward', 'outward'].includes(design.opening)) throw new Error('Unknown opening direction.');
  validateFrameDesign(design);
  const resolved = validateLeafGeometry(design);
  assemblyClearances(design);
  cassetteProfileMm(design.cassette, design);
  const inset = design.cassette.widthMm - design.cassette.overlapMm;
  resolved.openings.forEach((o, i) => {
    if (Math.min(o.widthMm, o.heightMm) <= 2 * inset + 1) {
      throw new Error(`Opening ${i + 1} is too small for the cassette (minimum above ${2 * inset + 1} mm).`);
    }
    const overlap = design.cassette.overlapMm;
    if (Math.min(o.xMm, o.yMm, design.widthMm - o.xMm - o.widthMm,
      resolved.leafTopMm - o.yMm - o.heightMm) < overlap) throw new Error(`Opening ${i + 1} cassette extends beyond the leaf edge.`);
    resolved.openings.slice(0, i).forEach((p) => {
      if (o.xMm < p.xMm + p.widthMm + 2 * overlap && o.xMm + o.widthMm + 2 * overlap > p.xMm &&
          o.yMm < p.yMm + p.heightMm + 2 * overlap && o.yMm + o.heightMm + 2 * overlap > p.yMm) {
        throw new Error('Window cassettes overlap. Increase the gap or reduce the overlap.');
      }
    });
  });
  return resolved;
}
